import { useEffect, useRef, useState, type ReactNode } from 'react';
import { css } from 'styled-system/css';
import { FormatStrip } from './FormatStrip';
import { FormulaAddrBar } from './FormulaAddrBar';
import { FormulaOverlay } from './FormulaOverlay';
import { NativeFormula } from './NativeFormula';

const FORMULA_EXPAND_DELAY_MS = 250;

export function EditorChrome({
  addr,
  expanded,
  active = true,
  children,
}: {
  addr: string;
  expanded?: boolean;
  active?: boolean;
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!expanded || !active) {
      setOpen(false);
      return;
    }

    const root = rootRef.current;
    if (!root) return;

    let timeoutId = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        window.clearTimeout(timeoutId);
        const visible =
          !!entry?.isIntersecting && entry.intersectionRatio >= 0.4;
        if (!visible) {
          setOpen(false);
          return;
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          setOpen(true);
          return;
        }
        timeoutId = window.setTimeout(
          () => setOpen(true),
          FORMULA_EXPAND_DELAY_MS,
        );
      },
      {
        threshold: [0.4, 0.6, 1],
        rootMargin: '0px 0px -12% 0px',
      },
    );
    observer.observe(root);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [expanded, active]);

  return (
    <div
      ref={rootRef}
      className={css({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        background: 'bg.surface',
      })}
    >
      <FormatStrip />
      <div
        className={css({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        })}
      >
        <FormulaAddrBar
          addr={addr}
          value={expanded && !open ? <NativeFormula compact /> : ''}
        />
        <div
          className={css({
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          })}
        >
          {children}
        </div>
        {expanded && <FormulaOverlay open={open} />}
      </div>
    </div>
  );
}
